/**
 * The reference-list entry for a picture, table, artwork or film, in the APA
 * house style this app follows.
 *
 * This is the deck's auto-generated reference slide, not the note under the
 * figure — the two share no formatter on purpose. Here the title is in sentence
 * case and italic, the author keeps their initials, the medium descriptor and
 * the site, museum or studio all appear, and the entry ends with its URL.
 *
 * Which template applies depends on what is known about the source, so a
 * citation carries a `referenceCategory` and the formatter picks from:
 *
 *   a. site_image      Author, A. (Year). *Title* [Image format]. Site. URL
 *   b. untitled_image  Author, A. (Year). [Description] [Image format]. Site. URL
 *   c. no_author_image *Title* [Image format]. (Year). Site. URL
 *   d. museum_artwork  Artist, A. (Year). *Title* [Medium]. Museum, city, country. URL
 *   e. magazine_image  Artist, A. (Year, Month Day). In Article. *Magazine*. URL
 *   f. …               An image with no artist of its own is not referenced as
 *                      an image at all: the underlying source is referenced
 *                      instead — a film, a webpage, a book, an article.
 *
 * The templates come from the college library's APA guidance for referencing
 * figures and tables.
 */

import {
  cleanValue,
  ensurePeriod,
  formatReferenceAuthor,
  formatYearMonthDay,
  formatYearOnly,
  mergeSegments,
  normalizeContributors,
  normalizeDoi,
  segmentsToText
} from './apaShared';

export const REFERENCE_CATEGORY = {
  SITE_IMAGE: 'site_image',
  UNTITLED_IMAGE: 'untitled_image',
  NO_AUTHOR_IMAGE: 'no_author_image',
  MUSEUM_ARTWORK: 'museum_artwork',
  MAGAZINE_IMAGE: 'magazine_image',
  FILM: 'film',
  WEBPAGE_SOURCE: 'webpage_source',
  BOOK_SOURCE: 'book_source',
  ARTICLE_SOURCE: 'article_source'
};

/** The picker in the citation editor, in the order the library's guide lists them. */
export const IMAGE_REFERENCE_CATEGORIES = [
  {
    value: REFERENCE_CATEGORY.SITE_IMAGE,
    label: 'Image from a site or database',
    hint: 'Author, A. (Year). Title of image [Image format]. Site Name. URL'
  },
  {
    value: REFERENCE_CATEGORY.UNTITLED_IMAGE,
    label: 'Image with no title',
    hint: 'Author, A. (Year). [Brief description] [Image format]. Site Name. URL'
  },
  {
    value: REFERENCE_CATEGORY.NO_AUTHOR_IMAGE,
    label: 'Image with no author',
    hint: 'Title of Image [Image format]. (Year). Site Name. URL'
  },
  {
    value: REFERENCE_CATEGORY.MUSEUM_ARTWORK,
    label: 'Artwork on a museum site',
    hint: 'Artist, A. (Year). Title of artwork [Medium]. Museum, city, country. URL'
  },
  {
    value: REFERENCE_CATEGORY.MAGAZINE_IMAGE,
    label: 'Image in a magazine',
    hint: 'Artist, A. (Year, Month Day). In Title of article. Title of Magazine. URL'
  },
  {
    value: REFERENCE_CATEGORY.FILM,
    label: 'Still from a film (no artist listed)',
    hint: 'Director, D. (Director). (Year). Title of film [Film]. Studio.'
  },
  {
    value: REFERENCE_CATEGORY.WEBPAGE_SOURCE,
    label: 'Photo inside a webpage (no artist listed)',
    hint: 'Referenced as the webpage it came from.'
  },
  {
    value: REFERENCE_CATEGORY.BOOK_SOURCE,
    label: 'Illustration in a book (no artist listed)',
    hint: 'Referenced as the book it came from.'
  },
  {
    value: REFERENCE_CATEGORY.ARTICLE_SOURCE,
    label: 'Image in a journal article (no artist listed)',
    hint: 'Referenced as the article it came from.'
  }
];

export const REFERENCE_CATEGORY_VALUES = IMAGE_REFERENCE_CATEGORIES.map((option) => option.value);

/**
 * An image with no artist of its own is referenced as its underlying source, so
 * these categories hand the record back to the ordinary reference formula for
 * that kind of work.
 */
const DELEGATED_SOURCE_TYPES = {
  [REFERENCE_CATEGORY.WEBPAGE_SOURCE]: 'webpage',
  [REFERENCE_CATEGORY.BOOK_SOURCE]: 'book',
  [REFERENCE_CATEGORY.ARTICLE_SOURCE]: 'journal'
};

/** Media descriptors the editor offers; any other text is accepted as typed. */
export const MEDIUM_OPTIONS = [
  'Photograph',
  'Painting',
  'Illustration',
  'Drawing',
  'Infographic',
  'Chart',
  'Map',
  'Sculpture',
  'Film',
  'Image'
];

const DEFAULT_IMAGE_MEDIUM = 'Image';

const hasAuthor = (meta = {}) => normalizeContributors(meta).length > 0;

/**
 * The template a record falls under when the author has not chosen one.
 *
 * Only the two the fields can actually prove are inferred — no author, and no
 * title. Everything else (a museum page, a magazine, a film still) is a
 * judgement about the source that only the author can make, so it stays an
 * explicit choice.
 */
export const inferReferenceCategory = (meta = {}) => {
  const explicit = cleanValue(meta.referenceCategory);
  if (REFERENCE_CATEGORY_VALUES.includes(explicit)) return explicit;

  if (/^film$/i.test(cleanValue(meta.medium))) return REFERENCE_CATEGORY.FILM;
  if (!hasAuthor(meta)) return REFERENCE_CATEGORY.NO_AUTHOR_IMAGE;
  if (!cleanValue(meta.referenceTitle) && !cleanValue(meta.title)) return REFERENCE_CATEGORY.UNTITLED_IMAGE;
  return REFERENCE_CATEGORY.SITE_IMAGE;
};

/**
 * The title as the reference list writes it: sentence case.
 *
 * A record keeps one title, and the slide note shows it in Title Case, so an
 * author who needs the two to differ ("Picture Perfect: The Making of Sleeping
 * Beauty" on the slide, "Picture perfect: The making of Sleeping Beauty" in the
 * list) supplies `referenceTitle`. Recasing it here is not possible without
 * lower-casing proper nouns, so the stored title is used verbatim otherwise.
 */
const referenceTitle = (meta = {}) => cleanValue(meta.referenceTitle) || cleanValue(meta.title);

// A block's caption is rich text, so its markup is dropped before it can stand
// in as a description.
const stripMarkup = (value) => cleanValue(cleanValue(value).replace(/<[^>]*>/g, ' ').replace(/\s{2,}/g, ' '));

const descriptiveTitle = (meta = {}) => {
  const description = stripMarkup(meta.description)
    || stripMarkup(meta.caption)
    || stripMarkup(meta.alt)
    || referenceTitle(meta);
  if (!description) return '';
  return /^\[.*\]$/.test(description) ? description : `[${description}]`;
};

const mediumDescriptor = (meta = {}, fallback = '') => {
  const medium = cleanValue(meta.medium) || fallback;
  return medium ? `[${medium}]` : '';
};

const containerName = (meta = {}) => cleanValue(meta.containerTitle)
  || cleanValue(meta.source)
  || cleanValue(meta.siteName)
  || cleanValue(meta.publisher);

const referenceLink = (meta = {}) => {
  const doi = normalizeDoi(meta.doi);
  if (doi) return `https://doi.org/${doi}`;
  return cleanValue(meta.url);
};

const joinChunks = (chunks) => {
  const segments = [];
  chunks
    .filter((chunk) => Array.isArray(chunk) && chunk.some((segment) => cleanValue(segment?.text)))
    .forEach((chunk, index) => {
      if (index > 0) segments.push({ text: ' ' });
      chunk.forEach((segment) => segments.push(segment));
    });
  return mergeSegments(segments);
};

const plain = (text) => (cleanValue(text) ? [{ text }] : []);
const italic = (text) => (cleanValue(text) ? [{ text, italic: true }] : []);

const directorLabel = (meta = {}) => (normalizeContributors(meta).length > 1 ? 'Directors' : 'Director');

const buildTemplate = (category, meta) => {
  const author = formatReferenceAuthor(meta);
  const year = formatYearOnly(meta.date, meta.year);
  const title = referenceTitle(meta);
  const container = containerName(meta);
  const link = referenceLink(meta);

  switch (category) {
    case REFERENCE_CATEGORY.UNTITLED_IMAGE: {
      const description = descriptiveTitle(meta);
      return joinChunks([
        plain(ensurePeriod(author)),
        plain(`(${year}).`),
        // A supplied description is not a title, so it stays roman inside its
        // brackets even though a real title would be italic.
        description ? [{ text: `${description} ${mediumDescriptor(meta, DEFAULT_IMAGE_MEDIUM)}`.trim() + '.' }] : [],
        plain(container ? `${container}.` : ''),
        plain(link)
      ]);
    }
    case REFERENCE_CATEGORY.NO_AUTHOR_IMAGE: {
      const descriptor = mediumDescriptor(meta, DEFAULT_IMAGE_MEDIUM);
      return joinChunks([
        title ? [...italic(title), ...plain(descriptor ? ` ${descriptor}.` : '.')] : [],
        plain(`(${year}).`),
        plain(container ? `${container}.` : ''),
        plain(link)
      ]);
    }
    case REFERENCE_CATEGORY.MUSEUM_ARTWORK: {
      const descriptor = mediumDescriptor(meta);
      return joinChunks([
        plain(ensurePeriod(author)),
        plain(`(${year}).`),
        title ? [...italic(title), ...plain(descriptor ? ` ${descriptor}.` : '.')] : [],
        plain(container ? `${container}.` : ''),
        plain(link)
      ]);
    }
    case REFERENCE_CATEGORY.MAGAZINE_IMAGE: {
      const article = cleanValue(meta.articleTitle) || title;
      return joinChunks([
        plain(ensurePeriod(author)),
        plain(`(${formatYearMonthDay(meta.date, meta.year)}).`),
        plain(article ? `In ${article}.` : ''),
        container ? [...italic(container), ...plain('.')] : [],
        plain(link)
      ]);
    }
    case REFERENCE_CATEGORY.FILM: {
      const studio = container;
      return joinChunks([
        author ? plain(`${author} (${directorLabel(meta)}).`) : [],
        plain(`(${year}).`),
        title ? [...italic(title), ...plain(' [Film].')] : [],
        plain(studio ? `${studio}.` : ''),
        plain(link)
      ]);
    }
    case REFERENCE_CATEGORY.SITE_IMAGE:
    default: {
      const descriptor = mediumDescriptor(meta, DEFAULT_IMAGE_MEDIUM);
      return joinChunks([
        plain(ensurePeriod(author)),
        plain(`(${year}).`),
        title ? [...italic(title), ...plain(descriptor ? ` ${descriptor}.` : '.')] : [],
        plain(container ? `${container}.` : ''),
        plain(link)
      ]);
    }
  }
};

/**
 * The reference entry for an image-like source.
 *
 * Returns `{ category, segments, text }`, or `{ category, delegateSourceType }`
 * when the record has no artist of its own and has to be referenced as the film,
 * webpage, book or article it came from. Returns null when the record holds
 * nothing worth listing.
 */
export const buildImageReference = (meta = {}) => {
  const category = inferReferenceCategory(meta);

  const delegateSourceType = DELEGATED_SOURCE_TYPES[category];
  if (delegateSourceType) return { category, delegateSourceType };

  const hasSomething = [
    formatReferenceAuthor(meta),
    referenceTitle(meta),
    containerName(meta),
    referenceLink(meta),
    cleanValue(meta.date) || cleanValue(meta.year)
  ].some(Boolean);
  if (!hasSomething) return null;

  const segments = buildTemplate(category, meta);
  const text = segmentsToText(segments);
  if (!cleanValue(text)) return null;

  return { category, segments, text };
};
